// src/components/heygen/HeyGenVideoPage.tsx
// Generate a talking-head video — async with polling.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Upload, Video,
} from 'lucide-react';

import {
  ApiError, listAvatars, listVoices, generateVideo, uploadAudio, checkJob,
  type HeyGenAvatar, type HeyGenVoice, type HeyGenJob,
} from '@/lib/api_heygen';
import { useJobPoller } from '@/hooks/useJobPoller';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function HeyGenVideoPage({ addNotification }: { addNotification?: Notifier }) {
  const [avatars, setAvatars] = useState<HeyGenAvatar[]>([]);
  const [voices, setVoices] = useState<HeyGenVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  // Form
  const [avatarId, setAvatarId] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [inputMode, setInputMode] = useState<'text' | 'audio'>('text');
  const [script, setScript] = useState('');
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [resolution, setResolution] = useState<'720p' | '1080p'>('1080p');
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const [background, setBackground] = useState('#0D0F1A');
  const [submitting, setSubmitting] = useState(false);
  const audioRef = useRef<HTMLInputElement>(null);

  // Job
  const [jobId, setJobId] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [thumbnail, setThumbnail] = useState<string | undefined>();

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setApiMissing(false);
    try {
      const [a, v] = await Promise.all([listAvatars(), listVoices()]);
      setAvatars(a);
      setVoices(v);
      if (a.length && !avatarId) setAvatarId(a[0].avatar_id);
      if (v.length && !voiceId) setVoiceId(v[0].voice_id);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error cargando avatares', message: e.message });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const poller = useJobPoller<HeyGenJob>({
    checkFn: checkJob,
    jobId,
    interval: 4000,
    onComplete: (r: HeyGenJob) => {
      setVideoUrl(r.video_url || null);
      setThumbnail(r.thumbnail_url);
      addNotification?.({ type: 'success', title: 'Video generado' });
    },
    onFail: (r: HeyGenJob) => {
      addNotification?.({ type: 'error', title: 'Error en video', message: r.error_message || 'Failed' });
    },
  });

  const handleSubmit = async () => {
    if (inputMode === 'text' && !script.trim()) return;
    if (inputMode === 'audio' && !audioFile) return;
    setSubmitting(true);
    setVideoUrl(null);
    setJobId(null);
    try {
      let audioUrl: string | undefined;
      if (inputMode === 'audio' && audioFile) {
        const up = await uploadAudio(audioFile);
        audioUrl = up.url;
      }
      const job = await generateVideo({
        avatar_id: avatarId,
        voice_id: voiceId,
        ...(inputMode === 'text' ? { script: script.trim() } : {}),
        ...(audioUrl ? { audio_url: audioUrl } : {}),
        resolution,
        aspect_ratio: aspectRatio,
        background_color: background,
      });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error creando video', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="HeyGen" />;
  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-white/40" /></div>;
  }

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Avatar grid */}
      <div>
        <label className="text-xs text-white/50 mb-2 block">Avatar</label>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto pr-1">
          {avatars.map((a) => (
            <button
              key={a.avatar_id}
              onClick={() => setAvatarId(a.avatar_id)}
              className={`rounded-lg overflow-hidden border-2 transition-all ${
                avatarId === a.avatar_id ? 'border-[var(--acc1)] shadow-lg shadow-[var(--acc1)]/20' : 'border-transparent hover:border-white/20'
              }`}
            >
              {a.thumbnail_url ? (
                <img src={a.thumbnail_url} alt={a.name} className="w-full h-20 object-cover" />
              ) : (
                <div className="w-full h-20 bg-white/10 flex items-center justify-center text-xs text-white/30">
                  {a.name.charAt(0)}
                </div>
              )}
              <p className="text-[10px] text-white/60 px-1 py-0.5 truncate">{a.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Voice */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Voz</label>
        <select
          value={voiceId}
          onChange={(e) => setVoiceId(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--acc1)]"
        >
          {voices.map((v) => (
            <option key={v.voice_id} value={v.voice_id}>
              {v.name}{v.language ? ` (${v.language})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Input mode toggle + input */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Input</label>
        <div className="flex gap-2 mb-2">
          {(['text', 'audio'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setInputMode(m)}
              className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                inputMode === m ? 'bg-[var(--acc2)]/30 text-[var(--acc2)]' : 'bg-white/5 text-white/50'
              }`}
            >
              {m === 'text' ? 'Script (texto)' : 'Audio (archivo)'}
            </button>
          ))}
        </div>
        {inputMode === 'text' ? (
          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)] resize-y"
            placeholder="Escribe el guion que dirá el avatar…"
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => audioRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 p-5 rounded-xl border-2 border-dashed border-white/15 bg-white/5 text-white/50 hover:border-[var(--acc1)]/50 hover:text-white/80 transition-colors"
            >
              <Upload size={18} />
              {audioFile ? audioFile.name : 'Click para subir audio'}
            </button>
            <input
              ref={audioRef}
              type="file"
              accept="audio/*"
              onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
              className="hidden"
            />
          </>
        )}
      </div>

      {/* Options row */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-white/50 mb-1 block">Resolución</label>
          <select
            value={resolution}
            onChange={(e) => setResolution(e.target.value as any)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="720p">720p</option>
            <option value="1080p">1080p</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">Aspecto</label>
          <select
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value as any)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="16:9">16:9</option>
            <option value="9:16">9:16 (Vertical)</option>
            <option value="1:1">1:1 (Cuadrado)</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">Background</label>
          <input
            type="color"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            className="w-full h-9 rounded-lg cursor-pointer bg-transparent border border-white/10"
          />
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || isPolling || (inputMode === 'text' && !script.trim()) || (inputMode === 'audio' && !audioFile)}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
        {isPolling ? 'Generando video…' : 'Generar video'}
      </button>

      {/* Polling status */}
      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Procesando video…'}
            {poller.status === 'completed' && 'Video listo'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {/* Result */}
      {videoUrl && (
        <div className="o-card border border-emerald-500/30">
          <VideoPlayer src={videoUrl} poster={thumbnail} />
        </div>
      )}
    </div>
  );
}
